import React from 'react'
import { Container, Box, Title, Text, Stack, Anchor, Group, Divider } from '@mantine/core'

export default function TermsOfServicePage() {
  const noteIcon = `${process.env.PUBLIC_URL}/tunistry-note-icon-white.svg`
  const EFFECTIVE_DATE = 'April 14, 2026'

  const Section = ({ title, children }) => (
    <Stack gap="sm" mb="xl">
      <Title order={3} style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 4 }}>{title}</Title>
      {children}
    </Stack>
  )

  const P = ({ children }) => (
    <Text size="sm" style={{ lineHeight: 1.75 }}>{children}</Text>
  )

  const UL = ({ items }) => (
    <Box component="ul" style={{ paddingLeft: 24, margin: '8px 0' }}>
      {items.map((item, i) => (
        <Box component="li" key={i} style={{ marginBottom: 6 }}>
          <Text size="sm" style={{ lineHeight: 1.7 }}>{item}</Text>
        </Box>
      ))}
    </Box>
  )

  return (
    <Box>
      {/* Header */}
      <Box component="header" style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}>
        <Container size="lg" py="sm">
          <Group justify="space-between" align="center">
            <Group align="center" gap="sm">
              <img src={noteIcon} alt="Tunistry note icon" width={28} height={28} style={{ display: 'block' }} />
              <Title order={2} style={{
                margin: 0, fontSize: 28, fontWeight: 700,
                fontFamily: 'Cairo, Arial, sans-serif', letterSpacing: 0.2,
              }}>
                Tunistry
              </Title>
            </Group>
          </Group>
        </Container>
      </Box>

      {/* Content */}
      <Container size="md" py={56} style={{ textAlign: 'left' }}>
        <Stack gap="xs" mb="xl">
          <Title order={1} style={{ fontSize: '2rem' }}>Terms of Service</Title>
          <Text size="sm" c="dimmed">Effective Date: {EFFECTIVE_DATE}</Text>
        </Stack>

        <P>
          Welcome to Tunistry. These Terms of Service ("Terms") govern your access to and use of the Tunistry
          platform, including our website, mobile applications, and all related services (collectively, the
          "Service"), operated by Tunistry ("we," "us," or "our"). Please read these Terms carefully before
          using the Service. By accessing or using the Service, you agree to be bound by these Terms and our{' '}
          <Anchor href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Anchor>
          . If you do not agree, do not use the Service.
        </P>

        <Divider my="xl" />

        <Section title="1. Eligibility">
          <P>
            You must be at least 13 years of age to use the Service. If you are under 18, you represent that
            you have your parent or guardian's permission to use the Service and that they have read and agreed
            to these Terms on your behalf. By using the Service, you represent and warrant that you meet all
            eligibility requirements.
          </P>
        </Section>

        <Section title="2. Accounts">
          <P>
            To access certain features, you must create an account. You agree to provide accurate, current,
            and complete information during registration and to keep your account information up to date. You
            are responsible for maintaining the confidentiality of your account credentials and for all
            activity that occurs under your account. Notify us immediately at support@tunistry.com if you
            suspect unauthorized access.
          </P>
          <P>
            We reserve the right to suspend or terminate accounts that violate these Terms, engage in
            fraudulent activity, or remain inactive for an extended period.
          </P>
        </Section>

        <Section title="3. License to Use the Service">
          <P>
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
            non-transferable, revocable license to access and use the Service for your personal,
            non-commercial purposes. You may not sublicense, sell, resell, copy, modify, create derivative
            works of, or otherwise exploit any portion of the Service without our express written permission.
          </P>
        </Section>

        <Section title="4. User Conduct">
          <P>You agree not to use the Service to:</P>
          <UL items={[
            'Violate any applicable law or regulation.',
            'Post, transmit, or share content that is unlawful, defamatory, obscene, harassing, threatening, or otherwise objectionable.',
            'Infringe any intellectual property rights of any party.',
            'Impersonate any person or entity, or falsely represent your affiliation with any person or entity.',
            'Attempt to gain unauthorized access to any system or network associated with the Service.',
            'Introduce malware, viruses, or any other harmful code.',
            'Scrape, crawl, or otherwise extract data from the Service by automated means without our prior written consent.',
            'Interfere with or disrupt the integrity or performance of the Service.',
            'Engage in any activity that constitutes spam or unsolicited communications.',
          ]} />
        </Section>

        <Section title="5. Content and Intellectual Property">
          <P><strong>Our content:</strong> All text, graphics, logos, audio, video, software, and other material
            made available through the Service, excluding User Content, is owned by or licensed to Tunistry
            and is protected by copyright, trademark, and other intellectual property laws. You may not use
            our branding, trademarks, or proprietary content without prior written consent.
          </P>
          <P><strong>User Content:</strong> You retain ownership of content you submit, post, or display through
            the Service ("User Content"). By submitting User Content, you grant Tunistry a worldwide,
            royalty-free, non-exclusive, sublicensable license to use, reproduce, modify, adapt, publish,
            translate, and display such content in connection with operating and improving the Service,
            including for AI personalization and recommendation purposes.
          </P>
          <P><strong>Feedback:</strong> Any feedback, suggestions, or ideas you provide to us may be used by
            Tunistry without restriction or compensation to you.
          </P>
        </Section>

        <Section title="6. Music and Licensing">
          <P>
            Music available through the Service is provided under licenses obtained from rights holders,
            distributors, or licensing bodies. You may only listen to music through the Service's authorized
            playback mechanisms. You may not download, reproduce, redistribute, or publicly perform music
            obtained through the Service except as expressly permitted. Any unauthorized use of music may
            violate applicable copyright law.
          </P>
          <P>
            Certain features of the Service incorporate <strong>YouTube API Services</strong> to provide music
            search and playback functionality. By using any feature of the Service that incorporates YouTube API
            Services, you also agree to be bound by the{' '}
            <Anchor href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
              YouTube Terms of Service
            </Anchor>.
          </P>
        </Section>

        <Section title="7. Subscriptions and Payments">
          <P>
            Certain features of the Service require a paid subscription. By subscribing, you authorize us (or
            our payment processor) to charge the applicable fees to your payment method on a recurring basis
            until you cancel. All fees are stated in U.S. dollars unless otherwise indicated. We reserve the
            right to change pricing upon reasonable advance notice.
          </P>
          <P>
            Subscriptions automatically renew unless cancelled before the renewal date. You may cancel at any
            time through your account settings. Except as required by law or as otherwise stated in these
            Terms, payments are non-refundable.
          </P>
        </Section>

        <Section title="8. Termination">
          <P>
            We may suspend or terminate your access to the Service at any time, with or without cause or
            notice. Upon termination, your license to use the Service ends immediately. You may delete your
            account at any time. Sections of these Terms that by their nature should survive termination
            (including Sections 9, 10, 11, 12, 13, and 14) will remain in effect.
          </P>
        </Section>

        <Section title="9. Disclaimers">
          <P>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
            FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. TUNISTRY DOES NOT WARRANT THAT THE
            SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY DEFECTS WILL BE CORRECTED.
            YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.
          </P>
          <P>
            AI-generated content, including DJ recommendations, track selections, and conversational responses,
            is provided for entertainment and informational purposes only. We do not guarantee the accuracy,
            completeness, or suitability of any AI-generated content.
          </P>
        </Section>

        <Section title="10. Limitation of Liability">
          <P>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, TUNISTRY AND ITS OFFICERS, DIRECTORS,
            EMPLOYEES, AGENTS, LICENSORS, AND SERVICE PROVIDERS SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING LOSS OF PROFITS,
            DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR
            INABILITY TO USE THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </P>
          <P>
            IN NO EVENT SHALL TUNISTRY'S TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR
            RELATED TO THE SERVICE EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO TUNISTRY IN THE
            TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
          </P>
        </Section>

        <Section title="11. Indemnification">
          <P>
            You agree to defend, indemnify, and hold harmless Tunistry and its officers, directors,
            employees, and agents from and against any claims, liabilities, damages, losses, and expenses
            (including reasonable attorneys' fees) arising out of or in any way connected with (a) your
            access to or use of the Service; (b) your User Content; (c) your violation of these Terms; or
            (d) your violation of any applicable law or the rights of any third party.
          </P>
        </Section>

        <Section title="12. Binding Arbitration and Class Action Waiver">
          <P>
            <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.</strong>
          </P>
          <P>
            <strong>Agreement to Arbitrate:</strong> You and Tunistry agree that any dispute, claim, or
            controversy arising out of or relating to these Terms or the Service (including the existence,
            validity, interpretation, breach, or termination thereof) shall be resolved exclusively by
            binding individual arbitration, except as set forth below. Arbitration is less formal than a
            lawsuit in court, uses a neutral arbitrator instead of a judge or jury, and is subject to very
            limited review by courts. The arbitrator may award the same damages and relief that a court could
            award, on an individual basis.
          </P>
          <P>
            <strong>Arbitration Rules:</strong> The arbitration shall be administered by the American
            Arbitration Association ("AAA") under its Consumer Arbitration Rules, or by JAMS under its
            Streamlined Arbitration Rules, as mutually agreed. The arbitration shall be conducted in English.
            If a hearing is required, it will take place in the county where you reside or via videoconference
            at the election of either party. The arbitrator's decision shall be final, binding, and
            enforceable as a judgment in any court of competent jurisdiction.
          </P>
          <P>
            <strong>Class Action Waiver:</strong> YOU AND TUNISTRY EACH AGREE THAT CLAIMS MAY ONLY BE BROUGHT
            IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS,
            CONSOLIDATED, OR REPRESENTATIVE ACTION. The arbitrator may not consolidate more than one person's
            claims or preside over any form of class or representative proceeding.
          </P>
          <P>
            <strong>Exceptions:</strong> Either party may (a) bring an individual action in small claims court
            for disputes within its jurisdiction, or (b) seek injunctive or other equitable relief in a court
            of competent jurisdiction to prevent the actual or threatened infringement, misappropriation, or
            violation of intellectual property rights or confidential information.
          </P>
          <P>
            <strong>Severability:</strong> If any part of this Section 12 (other than the class action
            waiver) is found unenforceable, that part shall be severed and the remaining provisions shall
            continue in effect. If the class action waiver is found unenforceable, the entire arbitration
            agreement shall be void.
          </P>
        </Section>

        <Section title="13. Governing Law">
          <P>
            These Terms shall be governed by and construed in accordance with the laws of the State of
            Oregon, without regard to its conflict of law principles. Subject to the arbitration agreement
            in Section 12, you and Tunistry each consent to exclusive jurisdiction and venue in the federal
            or state courts located in Oregon for any dispute not subject to arbitration.
          </P>
        </Section>

        <Section title="14. General Provisions">
          <P>
            <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy and any
            additional terms applicable to specific features, constitute the entire agreement between you and
            Tunistry regarding the Service.
          </P>
          <P>
            <strong>Modifications:</strong> We may update these Terms from time to time. We will provide
            notice of material changes by updating the effective date and, where appropriate, by notifying
            you via email or an in-app notification. Your continued use of the Service after the updated
            Terms become effective constitutes acceptance of the changes.
          </P>
          <P>
            <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms will not be
            considered a waiver of those rights.
          </P>
          <P>
            <strong>Severability:</strong> If any provision of these Terms is held to be invalid or
            unenforceable, the remaining provisions will remain in full force and effect.
          </P>
          <P>
            <strong>Assignment:</strong> You may not assign these Terms or any rights hereunder without our
            prior written consent. We may assign these Terms freely.
          </P>
          <P>
            <strong>Force Majeure:</strong> Tunistry shall not be liable for any failure or delay in
            performance resulting from causes beyond our reasonable control.
          </P>
        </Section>

        <Section title="15. Contact">
          <P>
            If you have questions about these Terms, please contact us at:
          </P>
          <P>
            <strong>Tunistry</strong><br />
            Email: <Anchor href="mailto:legal@tunistry.com">legal@tunistry.com</Anchor>
          </P>
        </Section>

        <Divider my="xl" />

        <Group gap="lg">
          <Anchor size="sm" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Anchor>
        </Group>
      </Container>

      {/* Footer */}
      <Box style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
        <Container size="lg" py="lg">
          <Group justify="center" gap="xl">
            <Text size="xs" c="dimmed">© {new Date().getFullYear()} Tunistry. All rights reserved.</Text>
            <Anchor size="xs" c="dimmed" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Anchor>
            <Anchor size="xs" c="dimmed" href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Anchor>
          </Group>
        </Container>
      </Box>
    </Box>
  )
}
